import {AccessService} from '../../../utils/AccessService';

/**
 * El Recorrido del AI Booster: Alimenta · Revisa · Memoria → Usa tu IA.
 *
 * UNA definición para las dos superficies que lo dibujan — el grupo de la barra lateral
 * (`SidebarV2`) y el hilo del escenario (`BoosterStage`). Hasta acá los gates vivían inline en el
 * sidebar; cuando el hilo apareció, copiarlos habría dejado dos criterios para la misma puerta, y
 * eso siempre termina contestando distinto (la regla que ya nos mordió con las pestañas Memoria e
 * IA que este grupo reemplazó).
 *
 * Los gates NO son nuevos: son los mismos con los que se mostraban las pestañas del tab bar del
 * cliente antes del paso 7 de la card gtAL5axI. El enforcement real vive en los endpoints; esto
 * sólo decide qué se dibuja.
 */

export const BOOSTER_STEP_FEED = 'alimenta';
export const BOOSTER_STEP_REVIEW = 'revisa';
export const BOOSTER_STEP_MEMORY = 'memoria';
export const BOOSTER_STEP_USE_IA = 'usa';

// Destino del Recorrido cuando no hay cliente en contexto. Hasta 2026-08-28 los pasos caían al
// Listado de Clientes, que es donde se elige uno pero no explica nada del Booster (card JCzWrSNo).
export const BOOSTER_NO_CLIENT_PATH = '/ia-booster';

// La ruta canónica del Recorrido desde que es UNA página que se scrollea (card LDdcpf52). Cada
// paso es un ancla, no una ruta: cambiar de hash sobre el mismo path NO remonta el elemento, así
// que el sidebar y los umbrales navegan sin perder el scroll ni re-pedir los datos de los actos.
export const BOOSTER_FLOW_SUFFIX = '/booster';

// Sub-anclas: lugares DENTRO de un acto. No son pasos —no aparecen en el hilo ni en el sidebar—
// pero son destino de deep-links vivos (la Bitácora la abren el Panel, el Listado v2 y el CC).
export const BOOSTER_ANCHOR_BITACORA = 'bitacora';
export const BOOSTER_ANCHOR_PROMPTS = 'prompts';

// Ancla → paso al que pertenece. Las de los actos se llaman igual que su paso a propósito: el
// ancla ES el paso, y una tabla con dos nombres para lo mismo se desincroniza sola.
const ANCHOR_TO_STEP = {
    [BOOSTER_STEP_FEED]: BOOSTER_STEP_FEED,
    [BOOSTER_STEP_REVIEW]: BOOSTER_STEP_REVIEW,
    [BOOSTER_STEP_MEMORY]: BOOSTER_STEP_MEMORY,
    [BOOSTER_STEP_USE_IA]: BOOSTER_STEP_USE_IA,
    [BOOSTER_ANCHOR_BITACORA]: BOOSTER_STEP_MEMORY,
    [BOOSTER_ANCHOR_PROMPTS]: BOOSTER_STEP_USE_IA,
};

// Vista de las rutas viejas → ancla del one-page. `null` = esa vista NO migra y sigue siendo
// pantalla propia: el flujo de carga (`setup`) porque es un formulario largo con su propio ida y
// vuelta, y el chat (`dashbito`) porque ocupa el alto útil y no puede vivir dentro de un scroll.
const VIEW_TO_ANCHOR = {
    sources: BOOSTER_STEP_FEED,
    setup: BOOSTER_STEP_FEED,
    facts: BOOSTER_STEP_REVIEW,
    resumen: BOOSTER_STEP_MEMORY,
    notes: BOOSTER_STEP_MEMORY,
    bitacora: BOOSTER_ANCHOR_BITACORA,
    dashbito: null,
    connect: BOOSTER_STEP_USE_IA,
    prompts: BOOSTER_ANCHOR_PROMPTS,
};

// `suffix` cuelga de `/client/:id`. `order` es el número que se dibuja en el hilo y en el sidebar:
// es fijo por paso y NO se recalcula sobre los pasos visibles — en nivel MEDIO no hay "Revisa", y
// renumerar dejaría a Memoria como "2" en unas agencias y como "3" en otras, con el mismo nombre.
// El destino no lleva número: no es un paso que se complete.
const STEP_DEFS = [
    {key: BOOSTER_STEP_FEED, order: 1, suffix: '/memory/sources', labelKey: 'sidebar_v2.booster_feed'},
    {key: BOOSTER_STEP_REVIEW, order: 2, suffix: '/memory/facts', labelKey: 'sidebar_v2.booster_review'},
    {key: BOOSTER_STEP_MEMORY, order: 3, suffix: '/memory', labelKey: 'sidebar_v2.booster_memory'},
    {key: BOOSTER_STEP_USE_IA, order: null, suffix: '/ia', labelKey: 'sidebar_v2.booster_use_ia'},
];

const STEP_BY_KEY = STEP_DEFS.reduce((acc, def) => ({...acc, [def.key]: def}), {});

/**
 * El cliente "en contexto": el de la pantalla en la que se está parado. Las rutas del cliente son
 * `/client/:id/...`; fuera de ellas no hay cliente.
 */
export function activeClientIdFrom(pathname) {
    const match = /^\/client\/(\d+)(\/|$)/.exec(pathname || '');
    return match ? match[1] : null;
}

/**
 * Qué pasos ve esta agencia, en orden. `modules` puede llegar `null` (los módulos todavía no
 * cargaron): se trata como lista vacía, que es fail-closed.
 */
export function boosterStepsFor({clientMemoryLevel, modules} = {}) {
    const mods = modules || [];
    const has = (mod) => mods.includes(mod);
    const memoryLevelOn = clientMemoryLevel === AccessService.MEMORY_LEVEL_MEDIO
        || clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL;

    const visible = {
        // Alimentar la memoria sólo tiene sentido si hay memoria.
        [BOOSTER_STEP_FEED]: memoryLevelOn,
        // La cola de curado no existe en MEDIO: ahí no hay hechos que aprobar.
        [BOOSTER_STEP_REVIEW]: clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL,
        // Con la Memoria en OFF la vista sobrevive por la Bitácora, que vive adentro.
        [BOOSTER_STEP_MEMORY]: memoryLevelOn || has(AccessService.MODULE_ACTIVITY_LOG),
        // Los mismos módulos que gateaban el tab IA.
        [BOOSTER_STEP_USE_IA]: has(AccessService.MODULE_AGENT) || has(AccessService.MODULE_DASHBO_MCP),
    };

    return STEP_DEFS.filter(def => visible[def.key]);
}

/**
 * ¿Existe la pestaña Historial del cliente (la Bitácora a pantalla completa, card 2BZI0Us8)?
 *
 * Es el MISMO gate que el acto Memoria, que es la otra puerta a esa vista: Memoria habilitada o el
 * módulo de Bitácora. Lo consultan el tab bar (`ClientTabs`) y la ruta (`ClientActivityLogContent`);
 * con dos copias, el día que una se apartara la pestaña abriría una pantalla que redirige.
 * No mira `ficha21`: eso lo pregunta cada consumidor, porque en el clásico la ruta es otra pantalla.
 */
export function hasHistorial(access) {
    return boosterStepsFor(access).some(def => def.key === BOOSTER_STEP_MEMORY);
}

/** ¿Existe el grupo AI Booster para esta agencia? */
export function hasBooster(access) {
    const steps = boosterStepsFor(access);
    return steps.some(def => def.key === BOOSTER_STEP_MEMORY || def.key === BOOSTER_STEP_USE_IA);
}

/**
 * Destino de un paso: el ancla del acto dentro del Recorrido. Sin cliente en contexto, la pantalla
 * propia del Booster.
 *
 * Antes de la one-page cada paso era una ruta (`/memory/facts`, `/ia`…). Esos sufijos siguen
 * declarados en `STEP_DEFS` porque `activeBoosterStep` los necesita para reconocer las rutas viejas
 * —que siguen vivas y redirigen acá—, pero ya no son a dónde se navega.
 */
export function boosterPathFor(stepKey, clientId) {
    const def = STEP_BY_KEY[stepKey];
    if (!def || !clientId) return BOOSTER_NO_CLIENT_PATH;
    return boosterFlowPath(clientId, def.key);
}

/** La ruta del Recorrido, opcionalmente apuntando a un ancla. */
export function boosterFlowPath(clientId, anchor) {
    const base = `/client/${clientId}${BOOSTER_FLOW_SUFFIX}`;
    return anchor ? `${base}#${anchor}` : base;
}

/** ¿Esta ruta es la one-page del Recorrido? */
export function isBoosterFlowPath(pathname) {
    const clientId = activeClientIdFrom(pathname || '');
    if (!clientId) return false;
    const rest = (pathname || '').slice(`/client/${clientId}`.length);
    return rest === BOOSTER_FLOW_SUFFIX || rest.startsWith(`${BOOSTER_FLOW_SUFFIX}/`);
}

/**
 * En qué paso cae un ancla. Devuelve `null` si el hash está vacío o no se reconoce — entrar a la
 * página sin ancla es estar arriba de todo, y ahí el paso activo lo decide qué sección se ve
 * (el observer del contenedor), no la URL.
 */
export function boosterStepFromHash(hash) {
    const anchor = String(hash || '').replace(/^#/, '');
    return ANCHOR_TO_STEP[anchor] || null;
}

// Los parámetros del contrato de la Bitácora. Están acá y no repetidos en cada consumidor porque
// llegar a la Bitácora con su deep-link son DOS puertas —el contenedor tiene que montar el panel
// sin esperar el scroll, y el duo del acto Memoria tiene que abrir en la bandeja de Historial— y
// las dos se abren con la misma condición. Duplicada, alcanzaba con que alguien sumara un
// parámetro de un solo lado para que el deep-link quedara a medias SIN fallar visiblemente.
const BITACORA_DEEP_LINK_PARAMS = ['layers', 'date_from', 'date_to'];

/**
 * ¿Esta URL pide la Bitácora ya filtrada? Es el contrato con el que el Panel, el Listado de
 * Clientes v2, la campanita y el Command Center la abren.
 *
 * Importa que se responda igual en los dos lugares que la consultan, porque `MemoryBitacoraView`
 * lee esos parámetros en su CONSTRUCTOR: si se monta tarde —o detrás de la bandeja equivocada— el
 * deep-link se pierde en silencio y la Bitácora aparece con los filtros guardados del usuario en
 * vez de los del link.
 */
export function hasBitacoraDeepLink(search, hash) {
    try {
        const params = new URLSearchParams(search || '');
        if (BITACORA_DEEP_LINK_PARAMS.some(key => params.get(key))) return true;
    } catch (e) {
        // Best-effort: una query que el navegador no puede parsear no es un deep-link.
    }
    return String(hash || '').replace(/^#/, '') === BOOSTER_ANCHOR_BITACORA;
}

/**
 * A dónde va una vista de las rutas viejas dentro del Recorrido. Devuelve `null` cuando esa vista
 * NO migra (sigue siendo su propia pantalla).
 *
 * La query se preserva entera y a propósito: `?layers=&date_from=&date_to=` es el contrato con el
 * que el Panel, el Listado v2, la campanita y el Command Center abren la Bitácora ya filtrada, y
 * `?connect=drive` es el atajo del menú "+ Agregar". Un redirect que los pierde rompe links que
 * están en producción.
 */
export function boosterFlowPathForView(view, clientId, search = '') {
    if (VIEW_TO_ANCHOR[view] === null) return null;
    const anchor = VIEW_TO_ANCHOR[view];
    const query = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : '';
    return `/client/${clientId}${BOOSTER_FLOW_SUFFIX}${query}${anchor ? `#${anchor}` : ''}`;
}

/**
 * ¿Esta ruta pertenece al AI Booster? Incluye la pantalla sin cliente, que NO es un paso.
 *
 * Son dos preguntas distintas y por eso son dos funciones. "¿Estoy adentro del Booster?" gobierna
 * que el grupo de la barra lateral se abra y se marque activo; "¿en qué paso estoy?" gobierna cuál
 * ítem se prende. En `/ia-booster` la primera es SÍ y la segunda es NINGUNO: no se eligió cliente,
 * así que no se entró a ningún paso. Mezclarlas hacía que `activeBoosterStep` devolviera "Alimenta"
 * para una pantalla donde Alimenta no se abrió.
 */
export function isBoosterPath(pathname) {
    const path = pathname || '';
    if (path === BOOSTER_NO_CLIENT_PATH || path.startsWith(`${BOOSTER_NO_CLIENT_PATH}/`)) return true;
    // La one-page entra por su propio path aunque no haya hash: sin ancla no hay paso activo, pero
    // se está adentro del Booster igual (el grupo del sidebar tiene que quedar abierto).
    if (isBoosterFlowPath(path)) return true;
    return activeBoosterStep(path) !== null;
}

/**
 * En qué paso del Recorrido cae una ruta. Devuelve `null` fuera del Booster **y también en la
 * pantalla sin cliente**, donde todavía no se entró a ningún paso.
 *
 * En la one-page el paso sale del ANCLA, no de la ruta: todos los actos comparten el mismo path.
 * Sin hash devuelve `null` — ver `boosterStepFromHash`.
 *
 * Ojo con el orden de las comparaciones: las vistas de la Memoria son HERMANAS bajo el mismo
 * prefijo (`/memory/sources`, `/memory/facts` y `/memory` a secas), así que un match por prefijo
 * hecho en el orden equivocado prendería "Memoria" también en Alimenta y en Revisa. Las vistas sin
 * paso propio —`notes`, `bitacora`— caen en Memoria, y `setup` (el flujo de carga) en Alimenta,
 * que es de donde se entra. Esas rutas siguen resolviéndose porque siguen existiendo: redirigen a
 * la one-page, y en el instante previo al redirect el sidebar tiene que dibujarse bien igual.
 */
export function activeBoosterStep(pathname, hash) {
    const path = pathname || '';
    const clientId = activeClientIdFrom(path);
    if (!clientId) return null;
    const rest = path.slice(`/client/${clientId}`.length);

    if (isBoosterFlowPath(path)) return boosterStepFromHash(hash);

    const isUnder = (suffix) => rest === suffix || rest.startsWith(`${suffix}/`);

    if (isUnder('/memory/sources') || isUnder('/memory/setup')) return BOOSTER_STEP_FEED;
    if (isUnder('/memory/facts')) return BOOSTER_STEP_REVIEW;
    if (isUnder('/memory')) return BOOSTER_STEP_MEMORY;
    if (isUnder('/ia')) return BOOSTER_STEP_USE_IA;
    return null;
}

export {STEP_DEFS as BOOSTER_STEP_DEFS};
